import type { VariantProps } from 'class-variance-authority';
import { useFormContext } from 'react-hook-form';

import type { comboBoxVariants } from '@/components/ui/ComboBox';
import ComboBox from '@/components/ui/ComboBox';
import type { FormOption, FormOptionGroups, FormOptions, GeneralFormProps } from '@/types/general';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '../ui/form';

type Props = {
  options: FormOptions | FormOptionGroups;
  loading?: boolean;
  multiple?: boolean;
  onSearchChange?: (searchTerm: string) => Promise<void> | void;
  onAddNew?: (value: string) => void;
  validateNewValue?: (value: string) => boolean;
  searchDebounceMs?: number;
  addNewLabel?: string;
  addNewPlaceholder?: string;
  alwaysShowAddNew?: boolean;
  placeholder?: string;
  variant?: VariantProps<typeof comboBoxVariants>['variant'];
  inputSize?: VariantProps<typeof comboBoxVariants>['inputSize'];
  disabled?: boolean;
  onChange?: (value: string | string[], fieldName?: string) => void;
  renderOption?: (option: FormOption, isSelected: boolean) => React.ReactNode;
  renderSelectedValue?: (option: FormOption) => React.ReactNode;
  startIcon?: string;
  endIcon?: string;
  onEndIconClick?: () => void;
  minSelections?: number;
  unknownOptionLabel?: string;
  loadMoreRef?: (node?: Element | null) => void;
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
  forceLoadingLabel?: string;
} & Omit<GeneralFormProps, 'onChange'>;

/**
 * RHF wrapper for ComboBox component
 * This component integrates the standalone ComboBox with React Hook Form
 */
function RHFShadcnComboBox({
  name,
  title,
  description,
  placeholder,
  loading = false,
  options,
  multiple = false,
  onSearchChange,
  onAddNew,
  validateNewValue,
  addNewLabel,
  variant = 'default',
  inputSize = 'default',
  searchDebounceMs,
  disabled = false,
  onChange,
  renderOption,
  renderSelectedValue,
  startIcon,
  endIcon,
  onEndIconClick,
  minSelections,
  unknownOptionLabel,
  loadMoreRef,
  isFetchingNextPage,
  hasNextPage,
  alwaysShowAddNew,
  forceLoadingLabel,
}: Props) {
  const { control } = useFormContext();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="mt-4 w-full">
          <FormControl>
            <ComboBox
              name={name}
              title={title}
              placeholder={placeholder}
              loading={loading}
              options={options}
              multiple={multiple}
              onSearchChange={onSearchChange}
              onAddNew={onAddNew}
              validateNewValue={validateNewValue}
              addNewLabel={addNewLabel}
              variant={variant}
              inputSize={inputSize}
              searchDebounceMs={searchDebounceMs}
              disabled={disabled}
              renderOption={renderOption}
              renderSelectedValue={renderSelectedValue}
              value={field.value}
              onChange={(value) => {
                if (onChange !== undefined) {
                  onChange(value, name);
                } else {
                  field.onChange(value);
                }
              }}
              startIcon={startIcon}
              endIcon={endIcon}
              onEndIconClick={onEndIconClick}
              minSelections={minSelections}
              unknownOptionLabel={unknownOptionLabel}
              loadMoreRef={loadMoreRef}
              isFetchingNextPage={isFetchingNextPage}
              hasNextPage={hasNextPage}
              alwaysShowAddNew={alwaysShowAddNew}
              forceLoadingLabel={forceLoadingLabel}
            />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export default RHFShadcnComboBox;
